import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, FileText, FolderKanban, ListChecks, Loader2, Search } from 'lucide-react';
import { http } from '@/lib/api';
import type { SearchResult } from '@/lib/types-tracking';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

const TYPE = {
  WORK: { label: 'Trabalho', icon: FolderKanban },
  TASK: { label: 'Tarefa', icon: ListChecks },
  DELIVERABLE: { label: 'Entrega', icon: FileText },
  CHECKPOINT: { label: 'Checkpoint', icon: CalendarClock },
} as const;

/** Busca global (somente entidades autorizadas pela API). Abre com Ctrl/⌘+K. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const listId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const results = useQuery({
    queryKey: ['search', debounced],
    enabled: open && debounced.length >= 2,
    queryFn: ({ signal }) => http.get<SearchResult[]>('/search', { q: debounced }, signal),
    staleTime: 10_000,
  });
  const items = results.data ?? [];
  const go = (r: SearchResult) => {
    setOpen(false);
    setQ('');
    navigate(r.link);
  };

  return (
    <>
      <Button variant="outline" size="sm" className="text-muted-foreground hidden w-56 justify-between font-normal 2xl:flex" onClick={() => setOpen(true)}>
        <span className="flex items-center gap-2">
          <Search /> Buscar…
        </span>
        <kbd className="bg-muted rounded px-1.5 text-[10px]">Ctrl K</kbd>
      </Button>
      <Button variant="ghost" size="icon" className="2xl:hidden" onClick={() => setOpen(true)} aria-label="Buscar">
        <Search />
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQ('');
        }}
      >
        <DialogContent className="top-[20%] translate-y-0 gap-0 p-0 sm:max-w-xl">
          <DialogTitle className="sr-only">Busca global</DialogTitle>
          <DialogDescription className="sr-only">Busque trabalhos, tarefas, entregas e checkpoints que você pode acessar.</DialogDescription>
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="text-muted-foreground size-4" aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded={items.length > 0}
              aria-controls={listId}
              aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
              aria-label="Buscar"
              placeholder="Buscar trabalhos, tarefas, entregas, checkpoints…"
              className="h-12 flex-1 bg-transparent text-sm outline-none"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, items.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                } else if (e.key === 'Enter' && items[active]) {
                  e.preventDefault();
                  go(items[active]!);
                }
              }}
            />
            {results.isFetching && <Loader2 className="text-muted-foreground size-4 animate-spin" />}
          </div>
          <ul id={listId} role="listbox" aria-label="Resultados" className="max-h-80 overflow-y-auto p-2">
            {debounced.length < 2 ? (
              <li className="text-muted-foreground p-3 text-sm">Digite ao menos 2 caracteres.</li>
            ) : results.isError ? (
              <li className="text-destructive p-3 text-sm">Falha na busca.</li>
            ) : !results.isFetching && items.length === 0 ? (
              <li className="text-muted-foreground p-3 text-sm">Nada encontrado.</li>
            ) : (
              items.map((r, i) => {
                const T = TYPE[r.type];
                return (
                  <li
                    key={`${r.type}-${r.id}`}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                    className={cn('flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm', i === active && 'bg-accent')}
                  >
                    <T.icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{r.title}</span>
                    <span className="text-muted-foreground text-xs">{T.label}</span>
                  </li>
                );
              })
            )}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
